/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { service } from '@ember/service';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type AgreementPageModel from 'garaje/models/agreement-page';
import type BadgeModel from 'garaje/models/badge';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import type FieldSetting from 'garaje/models/field-setting';
import type FlowVisitWorkflowModel from 'garaje/models/flow-visit-workflow';
import type GlobalFlowModel from 'garaje/models/global-flow';
import type IdScanPageModel from 'garaje/models/id-scan-page';
import type LocationModel from 'garaje/models/location';
import type PhotoPageModel from 'garaje/models/photo-page';
import PropagableModel from 'garaje/models/propagable';
import type SignInFieldActionRuleGroupModel from 'garaje/models/sign-in-field-action-rule-group';
import type SignInFieldPageModel from 'garaje/models/sign-in-field-page';
import type SummaryPageModel from 'garaje/models/summary-page';
import type UserModel from 'garaje/models/user';
import type UserDocumentTemplateConfiguration from 'garaje/models/user-document-template-configuration';
import type VisualComplianceConfigurationModel from 'garaje/models/visual-compliance-configuration';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { FlowType, STORE_RESPONSE_CONFIG_OPTIONS } from 'garaje/utils/enums';
import { equal } from 'macro-decorators';

import type SkinnyLocationModel from './skinny-location';

const FLOW = Object.freeze({
  customTranslations: () => ({ name: {} }),
  OVERWRITABLE_SETTINGS: {
    name: 'Name',
    enabled: 'Enable visitor type',
    additionalGuests: 'Plus one sign-in',
    useExistingPhoto: 'use existing photos',
  },
  PROPAGATED_RELOADABLE_RELATIONS: {
    belongsTo: ['agreementPage', 'summaryPage', 'photoPage'],
    hasMany: [],
  },
});

const ONBOARDING_CHECKS = {
  taskName: 'loadFlowsTask',
  attributes: ['name'],
};

interface SerializedFieldSetting {
  sign_in_field_id?: number | null;
  is_selected: boolean;
  data_category?: string | null;
}

interface SerializedFlowSettings {
  flow_id: string;
  settings: SerializedFieldSetting[];
}

class Flow extends PropagableModel {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: Store;

  @attr('string', { defaultValue: '' }) declare headerText: string;
  @attr('boolean', { defaultValue: false }) declare enabled: boolean;
  @attr('boolean', { defaultValue: true }) declare useDefaultHeaderText: boolean;
  @attr('string', { defaultValue: STORE_RESPONSE_CONFIG_OPTIONS.STORED }) declare storeResponseConfig: string;

  @attr('date') declare deletedAt: Date;

  @belongsTo('location', { async: true, inverse: 'flows' }) declare location: AsyncBelongsTo<LocationModel>;
  /**
   * used to link flows to included locations as they are loaded in as skinny-locations
   */
  @belongsTo('skinny-location', { async: false, inverse: 'flows' }) declare skinnyLocation: SkinnyLocationModel;

  @hasMany('skinny-location', { async: true, inverse: null }) declare locations: AsyncHasMany<SkinnyLocationModel>; // For Global Flows?
  @belongsTo('summary-page', { async: true, inverse: 'flow' }) declare summaryPage: AsyncBelongsTo<SummaryPageModel>;
  @belongsTo('agreement-page', { async: true, inverse: 'flow' })
  declare agreementPage: AsyncBelongsTo<AgreementPageModel>;
  @belongsTo('sign-in-field-page', { async: true, inverse: 'flow' })
  declare signInFieldPage: AsyncBelongsTo<SignInFieldPageModel>;
  @hasMany('sign-in-field-action-rule-group', { async: true, inverse: 'flow' })
  declare signInFieldActionRuleGroups: AsyncHasMany<SignInFieldActionRuleGroupModel>;
  @belongsTo('badge', { async: true, inverse: 'flow' }) declare badge: AsyncBelongsTo<BadgeModel>;
  @belongsTo('photo-page', { async: true, inverse: 'flow' }) declare photoPage: AsyncBelongsTo<PhotoPageModel>;
  @belongsTo('id-scan-page', { async: true, inverse: 'flow' }) declare idScanPage: AsyncBelongsTo<IdScanPageModel>;
  @belongsTo('document-upload-page', { async: true, inverse: null })
  declare documentUploadPage: AsyncBelongsTo<DocumentUploadPageModel>;
  @belongsTo('visual-compliance-configuration', { async: true, inverse: 'flow' })
  declare visualComplianceConfiguration: AsyncBelongsTo<VisualComplianceConfigurationModel>;
  @belongsTo('global-flow', { async: true, inverse: 'flows' }) declare globalFlow: AsyncBelongsTo<GlobalFlowModel>;
  @belongsTo('flow-visit-workflow', { async: true, inverse: 'flow' })
  declare flowVisitWorkflow: AsyncBelongsTo<FlowVisitWorkflowModel> | null;

  @hasMany('user-document-template-configuration', { async: true, inverse: null })
  declare userDocumentTemplateConfigurations: AsyncHasMany<UserDocumentTemplateConfiguration>;
  @hasMany('user-document-template-configuration', { async: true, inverse: null })
  declare activeUserDocumentTemplateConfigurations: AsyncHasMany<UserDocumentTemplateConfiguration>;
  @hasMany('user', { async: true, inverse: null }) declare visitorDocumentContacts: AsyncHasMany<UserModel>;

  @hasMany('user', { async: true, inverse: null }) declare inviteApprovalContacts: AsyncHasMany<UserModel>;

  @hasMany('field-setting', { async: true, inverse: null }) declare fieldSettings: AsyncHasMany<FieldSetting>;

  // START - removed from base class
  @attr('string') declare type: FlowType;
  @attr('number') declare position: number;
  @attr('number') declare blueprint: number | null;
  @attr('string', { defaultValue: '' }) declare description: string;
  @attr('boolean', { defaultValue: false }) declare idCheck: boolean;
  @attr('boolean', { defaultValue: false }) declare employeeCentric: boolean;
  @attr('immutable', { defaultValue: FLOW.customTranslations }) declare customTranslations: Record<string, unknown>;
  // END - removed from base class

  @attr('boolean', { defaultValue: false }) declare additionalGuests: boolean;
  @attr('boolean', { defaultValue: false }) declare inviteApprovalsEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare useExistingPhoto: boolean;
  @attr('boolean', { defaultValue: false }) declare visitorSurveyEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare visualComplianceEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare propertyInviteApprovalsEnabled: boolean;

  @attr('string', { defaultValue: 'all_visitors' }) declare welcomeEmailPreference: string;
  @attr('array', { defaultValue: () => [] }) declare mailerTemplatesToSkip: string[];

  @equal('type', FlowType.EMPLOYEE_SCREENING) isProtect!: boolean;

  isGlobal = false as const;

  @computed('type')
  get isGlobalChild(): boolean {
    const globalTypes: string[] = [FlowType.GLOBAL_CHILD];
    return globalTypes.includes(this.type);
  }

  get isWalkup(): boolean {
    return this.type === FlowType.PROPERTY_WALKUP;
  }

  get canEdit(): boolean {
    if (!this.type) {
      return this.abilities.can('edit visitor-types');
    } else if (this.type === FlowType.GLOBAL_CHILD) {
      return false;
    } else {
      return this.abilities.can('edit global-flows');
    }
  }

  get globalChildSiblings(): Flow[] {
    if (!this.isGlobalChild) return [];

    let globalFlowId: string | null = null;

    try {
      globalFlowId = (<Flow>this).belongsTo('globalFlow').id();
    } catch (_) {
      return [];
    }

    if (!globalFlowId) return [];

    return this.store.peekAll('flow').filter((flow) => {
      if (!flow.isGlobalChild) return false;

      try {
        return flow.belongsTo('globalFlow').id() === globalFlowId;
      } catch (_) {
        return false;
      }
    });
  }

  get visitWorkflowEnabled(): boolean {
    try {
      const visitWorkflowId = (<Flow>this).belongsTo('flowVisitWorkflow').id();
      return visitWorkflowId !== null;
    } catch (_) {
      return false;
    }
  }

  invitesTemplateUrl(): string {
    const path = 'csv-template';
    const adapter = this.store.adapterFor('flow');
    // Temporary workaround to switch the api version from v3 to v2 until the csv template
    // api catches up
    const url = adapter.buildURL('flow', this.id).replace('v3', 'v2');
    return `${url}/${path}`;
  }

  serializeDirtyFieldSettings(): SerializedFlowSettings | null {
    const fieldSettings = (<FieldSetting[] | null>(<Flow>this).hasMany('fieldSettings').value())?.slice();
    if (!fieldSettings) return null;

    const settings: SerializedFieldSetting[] = fieldSettings
      .filter((fs) => fs.hasDirtyAttributes)
      .map((fs) => {
        if (fs.signInFieldId !== null) {
          return { sign_in_field_id: fs.signInFieldId, is_selected: fs.isSelected };
        }
        return { data_category: fs.dataCategory, is_selected: fs.isSelected };
      });

    if (!settings.length) return null;

    return { flow_id: this.id, settings };
  }

  hasDirtyWelcomeEmailPreference(): boolean {
    const changedAttrs = this.changedAttributes();
    return Object.keys(changedAttrs).includes('welcomeEmailPreference');
  }

  syncActiveUserDocumentTemplateConfigurations(): void {
    // @ts-expect-error
    this.activeUserDocumentTemplateConfigurations = this.userDocumentTemplateConfigurations.content.filter(
      (config) => config.active,
    );
  }

  static PROPAGATED_RELOADABLE_RELATIONS = FLOW.PROPAGATED_RELOADABLE_RELATIONS;
  static OVERWRITABLE_SETTINGS = FLOW.OVERWRITABLE_SETTINGS;
  static ONBOARDING_CHECKS = ONBOARDING_CHECKS;
}

export default Flow;

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    flow: Flow;
  }
}
